import { useMemo, useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';

import { JobApplication } from '@restworld/utility-types';
import { imageVariant, useAnalytics } from '@restworld/utils-common';

import {
  candidateModalVariantAtom,
  candidateStateChangeAtom,
  selectedJobApplicationAtom,
  useCandidateStateChangeModal,
} from '../../job-listing/employer-job-listing.atoms';

function useSpontaneousCandidate(jobApplication: JobApplication) {
  const {
    inserted_at,
    worker: {
      user: { email, phone_number },
      name,
      surname,
      main_picture,
    },
    worker_distance_to_restaurant,
    restaurant,
    status_key,
    hidden,
  } = jobApplication;
  const { openCandidateModal } = useCandidateStateChangeModal();
  const setSelectedJobApplication = useSetAtom(selectedJobApplicationAtom);
  const [candidateModalVariant, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);
  const setCandidateStateChange = useSetAtom(candidateStateChangeAtom);

  const distance = useMemo(
    () => (worker_distance_to_restaurant ? `${(worker_distance_to_restaurant / 1000).toFixed(1)} km` : ''),
    [worker_distance_to_restaurant]
  );
  const isNewCandidate = useMemo(() => status_key === 'worker_jp_in_shortlist', [status_key]);

  const candidateInfo = useMemo(
    () => ({
      distanceToRestaurant: distance,
      email,
      firstName: name,
      lastName: surname,
      image: imageVariant(main_picture, 'sm'),
      phone: phone_number,
    }),
    [distance, email, main_picture, name, phone_number, surname]
  );

  const restaurantInfo = useMemo(
    () => ({
      address: restaurant?.address,
      logo: imageVariant(restaurant?.restaurant_logo, 'sm'),
      name: restaurant?.restaurant_name,
    }),
    [restaurant?.address, restaurant?.restaurant_logo, restaurant?.restaurant_name]
  );

  const { trackEvent } = useAnalytics();

  const handleClick = useCallback(() => {
    setSelectedJobApplication(jobApplication);
    if (candidateModalVariant !== 'spontaneous_candidate') setCandidateModalVariant('spontaneous_candidate');
    setCandidateStateChange('worker_jp_in_shortlist');
    openCandidateModal();
    trackEvent('visualised_spontaneous_candidate', {
      workerId: jobApplication?.worker?.user_id,
    });
  }, [
    candidateModalVariant,
    jobApplication,
    openCandidateModal,
    setCandidateModalVariant,
    setCandidateStateChange,
    setSelectedJobApplication,
    trackEvent,
  ]);

  return { insertedAt: inserted_at, candidateInfo, restaurantInfo, isNewCandidate, hidden, onClick: handleClick };
}

export default useSpontaneousCandidate;
